import React, { useState, useEffect } from 'react';
import {
  Button,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
} from '@mui/material';
import { Check, Close } from '@mui/icons-material';

import FormattedMessages from '../../../messages/admin/ComplianceFormattedMessages';
import useComplianceRequest from '../../../requests/ComplianceRequest';
import AppealRequestDialog from './appealRequestDialog';

const VettingDetails = ({ errorHandler, brand, options }) => {
  const [useCase, setUseCase] = useState({});
  const [feedback, setFeedback] = useState([]);
  const [appealData, setAppealData] = useState(null);
  const [vettingReport, setReport] = useState([]);
  const [requestFeedback, setRequestFeedback] = useState(false);
  const [requestReport, setRequestReport] = useState(false);
  const [open, setOpen] = useState(false);

  const {
    getBrandUseCase,
    getBrandFeedback,
    getVettingReport,
    resend2Fa,
    getAppeals,
    revetBrand,
  } = useComplianceRequest();

  useEffect(() => {
    const fetchBrandUseCase = async () => {
      try {
        const data = await getBrandUseCase(brand.brand_code, errorHandler);
        if (!data.error) setUseCase(data);
      } catch (e) {
        errorHandler(e);
      }
    };
    fetchBrandUseCase();
  }, [brand]);

  const handleRequestBrandFeedback = async () => {
    try {
      setRequestFeedback(false);
      setFeedback([]);
      const data = await getBrandFeedback(brand.brand_code, errorHandler);
      const appeals = await getAppeals(brand.brand_id, errorHandler);
      console.log('appeals', appeals);

      setFeedback(data.category || []);
      setRequestFeedback(true);
      setAppealData(appeals);
    } catch (e) {
      errorHandler(e);
    }
  };

  const handleRequestVettingReport = async () => {
    try {
      setReport([]);
      setRequestReport(false);
      const data = await getVettingReport(brand.brand_code, errorHandler);

      setReport(data || []);
      setRequestReport(true);
    } catch (e) {
      errorHandler(e);
    }
  };

  const generateExternalVettingReport = () => {
    let reportElements;
    if (requestReport) {
      reportElements =
        vettingReport.length > 0 ? (
          vettingReport.map((item) => {
            return (
              <Stack key={item.id}>
                <Typography variant="h5" gutterBottom sx={{ paddingTop: 2 }}>
                  • {item.fileName}
                </Typography>
              </Stack>
            );
          })
        ) : (
          <Typography variant="h5" gutterBottom sx={{ paddingTop: 2 }}>
            No external vetting report found.
          </Typography>
        );
    }
    return (
      <Stack direction="column" spacing={2}>
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Typography variant="h2" gutterBottom sx={{ paddingTop: 2 }}>
            {FormattedMessages.vettingReportTitle}
          </Typography>
          {brand.entity_type === 'PUBLIC_PROFIT' ? (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => resend2Fa(brand.brand_id, errorHandler)}
              size="medium"
            >
              Resend 2FA
            </Button>
          ) : (
            <></>
          )}
        </Stack>
        {reportElements}
        <Stack direction="row" spacing={2} sx={{ marginY: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleRequestVettingReport}
            disabled
          >
            {FormattedMessages.requestButton}
          </Button>
          <Button variant="contained" color="secondary" disabled>
            {FormattedMessages.appealButton}
          </Button>
        </Stack>
      </Stack>
    );
  };

  const generateBrandFeedback = () => {
    let feedbackElements;
    if (requestFeedback) {
      feedbackElements =
        feedback.length > 0 ? (
          feedback.map((item) => {
            const relatedFields = item.fields.join(', ');
            return (
              <Box key={item.id}>
                <Typography variant="h5" gutterBottom sx={{ paddingTop: 2 }}>
                  • {item.description}
                </Typography>
                <Stack direction="row" spacing={1} gutterBottom marginLeft={2}>
                  <Typography variant="h6">Related Fields:</Typography>
                  <Typography variant="h6">{relatedFields}</Typography>
                </Stack>
              </Box>
            );
          })
        ) : (
          <Typography variant="h5" gutterBottom sx={{ paddingTop: 2 }}>
            No feedback found.
          </Typography>
        );
    }
    return (
      <>
        <Typography variant="h2" gutterBottom sx={{ paddingTop: 2 }}>
          {FormattedMessages.brandFeedbackTitle}
        </Typography>
        {feedbackElements}
        <Stack direction="row" spacing={2} sx={{ paddingTop: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleRequestBrandFeedback}
          >
            {FormattedMessages.requestButton}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setOpen(true)}
          >
            {FormattedMessages.appealButton}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => revetBrand(brand.brand_id, errorHandler)}
          >
            {'Revet Brand'}
          </Button>
        </Stack>
        {generateAppealData()}
      </>
    );
  };

  const generateAppealData = () => {
    if (!appealData) {
      return null;
    }

    return (
      <Table sx={{ marginY: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Appeal Status</TableCell>
            <TableCell>Explanation</TableCell>
            <TableCell>Outcome</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {appealData.map((appeal, index) => (
            <TableRow key={index}>
              <TableCell>{appeal.appealStatus}</TableCell>
              <TableCell>{appeal.explanation}</TableCell>
              <TableCell>{appeal?.appealOutcome?.vetStatus || 'N/A'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const generateUseCaseMatrix = () => {
    const mnos = {
      'AT&T': 10017,
      'T-Mobile': 10035,
      'US Cellular': 10037,
      'Verizon Wireless': 10038,
      ClearSky: 10631,
      Interop: 10901,
    };

    // Extract MNO IDs correctly
    const mnoIds = Object.values(mnos);
    const mnoKeys = Object.keys(mnos);

    let useCaseList = [];
    for (const [key, value] of Object.entries(useCase)) {
      useCaseList.push(
        <TableRow key={key}>
          <TableCell>{value?.usecase}</TableCell>
          <TableCell>
            {value?.mnoMetadata[mnoIds[0]]?.mnoSupport ? (
              <Check color="secondary" />
            ) : (
              <Close color="primary" />
            )}
          </TableCell>
          <TableCell>
            {value?.mnoMetadata[mnoIds[1]]?.mnoSupport ? (
              <Check color="secondary" />
            ) : (
              <Close color="primary" />
            )}
          </TableCell>
          <TableCell>
            {value?.mnoMetadata[mnoIds[2]]?.mnoSupport ? (
              <Check color="secondary" />
            ) : (
              <Close color="primary" />
            )}
          </TableCell>
          <TableCell>
            {value?.mnoMetadata[mnoIds[3]]?.mnoSupport ? (
              <Check color="secondary" />
            ) : (
              <Close color="primary" />
            )}
          </TableCell>
          <TableCell>
            {value?.mnoMetadata[mnoIds[4]]?.mnoSupport ? (
              <Check color="secondary" />
            ) : (
              <Close color="primary" />
            )}
          </TableCell>
          <TableCell>
            {value?.mnoMetadata[mnoIds[5]]?.mnoSupport ? (
              <Check color="secondary" />
            ) : (
              <Close color="primary" />
            )}
          </TableCell>
        </TableRow>
      );
    }
    return (
      <>
        <Typography variant="h2" gutterBottom sx={{ paddingTop: 4 }}>
          {FormattedMessages.usecaseMatrix}
        </Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{FormattedMessages.usecase}</TableCell>
              <TableCell>{mnoKeys[0]}</TableCell>
              <TableCell>{mnoKeys[1]}</TableCell>
              <TableCell>{mnoKeys[2]}</TableCell>
              <TableCell>{mnoKeys[3]}</TableCell>
              <TableCell>{mnoKeys[4]}</TableCell>
              <TableCell>{mnoKeys[5]}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{useCaseList}</TableBody>
        </Table>
      </>
    );
  };

  return (
    <Box>
      {generateExternalVettingReport()}
      {generateBrandFeedback()}
      <AppealRequestDialog
        open={open}
        onClose={() => setOpen(false)}
        errorHandler={errorHandler}
        brandId={brand.brand_id}
        appealCategories={options?.brandAppealCategory || []}
      />
      {generateUseCaseMatrix()}
    </Box>
  );
};

export default VettingDetails;
